//! Flatten a diagnostics `DiagTree` into the ordered, indented list of display
//! lines that the results window shows, so the "Save" feature can serialise the
//! *displayed* output to file in any format (mirroring the C++
//! `colour_line_disp_control::save_display_as_*`, which serialised its on-screen
//! line list).
//!
//! This is the single source of display truth shared with `DiagnosticsWindow` /
//! `DiagTreeNodeView`: it reuses the same grouping (`groupChildrenByRelate`),
//! sorting (`diagSort`) and row formatting so a saved report is byte-for-byte
//! what the user sees. The Rust `save_diag_report` command consumes the
//! resulting `ReportLine[]` and only deals with format encoding (UTF-16 text,
//! WordprocessingML, OpenDocument, PDF).

import { DiagTree, DiagTreeNode, DiagnosticMsg } from "../types/diag";
import { QueryType } from "../types/dialog-types";
import { groupChildrenByRelate } from "../components/DiagTreeNodeView";
import { sortRootsForQuery, sortRootsByOccurrences, sortOrphansForQuery } from "./diagSort";
import { formatTigerDateTime } from "./tigerTime";
import { severityColor } from "./diagSeverityColor";
import { getTigerResource, tigerFormat } from "./tigerResource";

/// One serialisable display line: `indent` is the leading-space count (the
/// character-grid equivalent of the on-screen pixel indent), `text` is the
/// rendered row text, and `color` is a 6-hex-digit "rrggbb" (no leading '#').
export interface ReportLine {
  indent: number;
  text: string;
  color: string;
}

// Indentation, in characters, mirroring `DiagTreeNodeView`'s pixel steps
// (CONTENT_OFFSET_PX 20 ≈ 3 chars, CHAR_PX 7): relative to a parent row's text
// column a normal child is +3, a titled-group header +4 with its children +8,
// and an occurrence-top's children +10.
const STEP_NORMAL = 3;
const STEP_TITLE_HEADER = 4;
const STEP_TITLE_CHILD = 8;
const STEP_OCCURRENCES_CHILD = 10;

// Group-title headers ("Determined by", "Accounts for", …) render in the UI's
// neutral grey (#374151); orphan rows in the muted grey (#666).
const TITLE_COLOR = "374151";
const ORPHAN_COLOR = "666666";

// CSS colour names emitted by `severityColor` → "rrggbb". Anything already in
// "#rrggbb" form is parsed directly; unknown values fall back to black.
const COLOR_NAME_HEX: Record<string, string> = {
  black: "000000",
  brown: "a52a2a",
  red: "ff0000",
  orange: "ffa500",
  purple: "800080",
  darkcyan: "008b8b",
  darkgreen: "006400",
  blue: "0000ff",
};

function colorToHex(color: string): string {
  if (color.startsWith("#")) {
    const h = color.slice(1);
    if (h.length === 6) return h.toLowerCase();
    if (h.length === 3) {
      return h
        .toLowerCase()
        .split("")
        .map((c) => c + c)
        .join("");
    }
  }
  return COLOR_NAME_HEX[color] ?? "000000";
}

// Render one node's row text exactly as `DiagTreeNodeView` does.
function rowText(
  node: DiagTreeNode,
  queryType: QueryType,
  depth: number,
  turbinePrefix: string,
): string {
  const isTitleRow = node.fault_type === "AR" || node.fault_type === "TI";
  const isLatched = node.relate_type === "LA";
  const isOccurrencesTop = node.relate_type === "OT";
  const hideTime = queryType === "Invalid" && depth >= 1;

  if (isTitleRow) return `${turbinePrefix}${node.diag_message}`;
  if (isLatched) return `[${node.latch_count}] ${turbinePrefix}${node.diag_message}`;
  if (isOccurrencesTop) return `${node.latch_count}    ${turbinePrefix}${node.diag_message}`;
  if (hideTime) return `${turbinePrefix}${node.diag_message}`;
  return `${formatTigerDateTime(node.diag_time)} ${turbinePrefix}${node.diag_message}`;
}

export interface FlattenOptions {
  tree: DiagTree;
  queryType: QueryType;
  // Whole-site queries prefix each row with the turbine short name.
  showTurbineName: boolean;
  turbineShortNames: Map<string, string>;
  // Levels of children to expand below the roots (C++ `expand_all_diagnostics_
  // level` / the directive's `expand_level`): 0 = roots only, N = depths 1..N.
  // Omitted (interactive Save) means fully expanded.
  expandLevel?: number;
}

export function flattenDiagReport(opts: FlattenOptions): ReportLine[] {
  const { tree, queryType, showTurbineName, turbineShortNames } = opts;
  const expandLevel = opts.expandLevel ?? Number.POSITIVE_INFINITY;
  const lines: ReportLine[] = [];

  const prefixFor = (msg: DiagnosticMsg): string => {
    const label = showTurbineName ? turbineShortNames.get(msg.config_name) ?? "" : "";
    return label ? `${label} - ` : "";
  };

  // Recurse a node, threading its text column (in chars). Mirrors
  // `DiagTreeNodeView` recursion + `groupChildrenByRelate`.
  const emit = (node: DiagTreeNode, col: number, depth: number): void => {
    lines.push({
      indent: col,
      text: rowText(node, queryType, depth, prefixFor(node)),
      color: colorToHex(severityColor(node.severity)),
    });

    // Honour the diagnostics expand level: a node only reveals its children
    // while its own depth is below the level (depth 0 roots → level 1 shows
    // their children, etc.).
    if (depth >= expandLevel) return;

    const isOccurrencesTop = node.relate_type === "OT";
    for (const group of groupChildrenByRelate(node.children)) {
      if (group.title !== null) {
        lines.push({ indent: col + STEP_TITLE_HEADER, text: group.title, color: TITLE_COLOR });
        for (const child of group.items) emit(child, col + STEP_TITLE_CHILD, depth + 1);
      } else {
        const childCol = col + (isOccurrencesTop ? STEP_OCCURRENCES_CHILD : STEP_NORMAL);
        for (const child of group.items) emit(child, childCol, depth + 1);
      }
    }
  };

  // Top-level split mirrors `DiagnosticsWindow`: chronological roots first, then
  // a "Latched Messages" section (relate_type "LA", sorted by occurrences),
  // then orphans.
  const plainRoots: DiagTreeNode[] = [];
  const latchedRoots: DiagTreeNode[] = [];
  for (const r of tree.roots) (r.relate_type === "LA" ? latchedRoots : plainRoots).push(r);

  for (const root of sortRootsForQuery(plainRoots, queryType)) emit(root, 0, 0);

  if (latchedRoots.length > 0) {
    lines.push({ indent: 0, text: getTigerResource("TIGER_DIAG_LATCH_MSG"), color: TITLE_COLOR });
    for (const root of sortRootsByOccurrences(latchedRoots)) emit(root, 0, 0);
  }

  const orphans = sortOrphansForQuery(tree.orphans, queryType);
  if (orphans.length > 0) {
    lines.push({
      indent: 0,
      text: tigerFormat(getTigerResource("TIGER_DIAG_ORPHANS_COUNT"), String(orphans.length)),
      color: TITLE_COLOR,
    });
    for (const m of orphans) {
      lines.push({
        indent: STEP_NORMAL,
        text: `${formatTigerDateTime(m.diag_time)} ${prefixFor(m)}${m.diag_message}  → ${m.relate_id}`,
        color: ORPHAN_COLOR,
      });
    }
  }

  return lines;
}
